import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';

export interface FinanceDateRangeProps {
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  /**
   * When true, render floating "From"/"To" input labels (Expense/Supplier
   * style). When false (default) the inputs are bare with a "to" separator
   * between them (EOD History / Aggregators style).
   */
  showLabels?: boolean;
  fromLabel?: string;
  toLabel?: string;
  fromTestId?: string;
  toTestId?: string;
  /** Extra styles for the wrapping Box (e.g. mb, flex-basis overrides). */
  sx?: SxProps<Theme>;
}

/**
 * Shared from/to date-range pair for every finance screen.
 *
 * Mobile-safe by construction: both inputs flex from content size and can
 * shrink (`minWidth: 0`, no fixed pixel widths), the pair wraps onto its own
 * row inside a wrapping filter bar, and the value text scales down slightly
 * on narrow viewports so "1 Jul 2026" never spills outside the input border
 * or collides with sibling controls.
 */
const FinanceDateRange: React.FC<FinanceDateRangeProps> = ({
  from,
  to,
  onFromChange,
  onToChange,
  showLabels = false,
  fromLabel = 'From',
  toLabel = 'To',
  fromTestId = 'date-from',
  toTestId = 'date-to',
  sx,
}) => {
  const { theme } = useTheme();
  const input = theme.components?.input;

  const fieldSx = {
    // Grow to share the row, but always allowed to shrink — width comes from
    // the flex basis + content, never a fixed pixel width.
    flex: '1 1 122px',
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: input?.background || theme.gray?.[100] || '#F0F0F0',
      borderRadius: '10px',
      '& fieldset': { borderColor: input?.border || theme.gray?.[200] || '#E0E0E0' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: input?.focus || theme.primary },
    },
    '& .MuiInputBase-input': {
      color: theme.text,
      fontFamily: 'inherit',
      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
      minWidth: 0,
      minHeight: '28px',
      px: 1.25,
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
  };

  return (
    <Box
      data-testid="finance-date-range"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        minWidth: 0,
        flex: '1 1 auto',
        ...sx,
      }}
    >
      <TextField
        type="date"
        label={showLabels ? fromLabel : undefined}
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        size="small"
        InputLabelProps={showLabels ? { shrink: true } : undefined}
        inputProps={{ 'aria-label': `${fromLabel} date` }}
        data-testid={fromTestId}
        sx={fieldSx}
      />
      {!showLabels && (
        <Typography sx={{ color: theme.gray?.[500] || '#666', fontSize: 14, flexShrink: 0 }}>
          to
        </Typography>
      )}
      <TextField
        type="date"
        label={showLabels ? toLabel : undefined}
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        size="small"
        InputLabelProps={showLabels ? { shrink: true } : undefined}
        inputProps={{ 'aria-label': `${toLabel} date`, min: from || undefined }}
        data-testid={toTestId}
        sx={fieldSx}
      />
    </Box>
  );
};

export default FinanceDateRange;
